import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  fetchMyLeagues, fetchUserTeams, fetchDmThreads, fetchInbox,
  fetchUpcomingGames, decideInbox, fetchFriends,
  fetchToday, markLeagueRead,
} from "../lib/api";
import { useUserHub } from "../hooks/useUserHub";
import { AvatarChip } from "./AvatarChip";
import { DmModal } from "./DmModal";
import { GloryBadgesGrid } from "./GloryBadgesGrid";
import { ReferralCard } from "./ReferralCard";
import { PublicLinkCard } from "./PublicLinkCard";
import { UpcomingSessionsCard } from "./UpcomingSessionsCard";
import { TipsLeaderboard } from "./TipsLeaderboard";
import { SeasonRecapShareModal } from "./SeasonRecapShareModal";
import { TeamHqModal } from "./TeamHqModal";
import { FriendsModal } from "./FriendsModal";
import { TierBadge } from "./TierBadge";
import { TierProgressBar } from "./TierProgressBar";
import { CombatStatsCard } from "./CombatStatsCard";
import { ClutchMomentsRail } from "./ClutchMomentsRail";
import { OnboardingChecklist } from "./OnboardingChecklist";
import { LiveStreamersTicker } from "./LiveStreamersTicker";
import { TodayTab } from "./TodayTab";
import { RecentResultsBlock } from "./RecentResultsBlock";
import { PremiumExportsCard } from "./PremiumExportsCard";
import { SeasonPassBanner } from "./SeasonPassBanner";
import { StreakChip } from "./StreakChip";
import { RivalsCard } from "./RivalsCard";
import { SampleLeagueCta } from "./SampleLeagueCta";
import { YourCardPreview } from "./dashboard/YourCardPreview";
import { InboxBlock } from "./dashboard/InboxBlock";
import { MyClipsBlock } from "./dashboard/MyClipsBlock";
import CreateTeamModal from "./CreateTeamModal";
import { ScheduleMatchModal } from "./dashboard/ScheduleMatchModal";
import { GracePeriodBanner, CancelMembershipButton } from "./dashboard/GracePeriodBanner";

const LEFT_LOCKED = true;   // left column is non-movable (profile/PRO/referral/shortcuts)

const STORAGE_KEY = "lhub_dash_order_v2";
const DEFAULT_ORDER = {
  // Trimmed to mirror the WinterHorse reference: profile + pro + friends
  // + referral on the left; the noisy `exports` and `shortcuts` blocks
  // were duplicates of premium messaging and a low-signal scratch pad.
  left:   ["profile", "pro", "public_link", "friends", "referral"],
  // Center is now hero-first (greeting → holo card → glory grid),
  // matching the reference. The `today` KPI banner + 4-tile grid + the
  // `upcoming` rail were AI-slop "stat sheets" that crowded the hero.
  center: ["greeting", "card", "upcoming_sessions", "glory"],
  right:  ["inbox", "recent", "rivals", "leagues", "clips", "hq"],
};

function loadOrder(serverLayout) {
  // Server-saved layout takes priority — it's the source of truth and syncs
  // across devices. Falls back to localStorage for offline / pre-sync state.
  let parsed = null;
  try {
    if (serverLayout && typeof serverLayout === "object") {
      parsed = serverLayout;
    } else {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) parsed = JSON.parse(raw);
    }
  } catch { /* ignore */ }
  if (!parsed) return DEFAULT_ORDER;
  const known = new Set(Object.values(DEFAULT_ORDER).flat());
  const leftIds = new Set(DEFAULT_ORDER.left);
  // Wave A — block IDs we want pinned to the TOP of their column regardless
  // of where they sit in a returning user's saved layout. Filtering them
  // out of `parsed` first lets the PIN_TOP loop below re-insert them at the
  // front unconditionally (handles users whose layout was auto-saved with
  // 'today' at the bottom on first render after Wave A shipped).
  const PIN_TOP = new Set([]);
  const cleaned = { ...parsed };
  for (const col of ["center", "right"]) {
    cleaned[col] = (parsed[col] || []).filter((id) => !PIN_TOP.has(id));
  }
  const out = { left: [], center: [], right: [] };
  const seen = new Set();
  for (const id of DEFAULT_ORDER.left) { out.left.push(id); seen.add(id); }
  // Pin Wave A's new blocks at the top.
  for (const col of ["center", "right"]) {
    for (const id of DEFAULT_ORDER[col]) {
      if (PIN_TOP.has(id)) { out[col].push(id); seen.add(id); }
    }
  }
  for (const col of ["center", "right"]) {
    for (const id of cleaned[col] || []) {
      if (!known.has(id) || seen.has(id)) continue;
      if (leftIds.has(id)) continue;
      out[col].push(id); seen.add(id);
    }
  }
  for (const col of ["center", "right"]) {
    for (const id of DEFAULT_ORDER[col]) {
      if (!seen.has(id)) { out[col].push(id); seen.add(id); }
    }
  }
  return out;
}
function saveOrder(order) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(order)); } catch { /* ignore */ }
}

function DraggableBlock({ id, col, setOrder, children }) {
  const [over, setOver] = useState(null);  // 'before' | 'after' | null

  const onDrop = (e) => {
    e.preventDefault();
    setOver(null);
    let src; try { src = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
    if (!src?.id || src.id === id) return;
    // Decide which half of the target was hit so the user can place items
    // BOTH above and below an existing block.
    const rect = e.currentTarget.getBoundingClientRect();
    const dropAfter = (e.clientY - rect.top) > (rect.height / 2);
    setOrder((prev) => {
      const next = { left: [...prev.left], center: [...prev.center], right: [...prev.right] };
      next[src.col] = next[src.col].filter((x) => x !== src.id);
      const tgt = next[col];
      let insertAt = tgt.indexOf(id);
      if (insertAt < 0) insertAt = tgt.length;
      if (dropAfter) insertAt += 1;
      tgt.splice(insertAt, 0, src.id);
      next[col] = tgt;
      return next;
    });
  };

  return (
    <div
      draggable
      data-testid={`dash-drag-${id}`}
      onDragStart={(e) => {
        e.dataTransfer.setData("text/plain", JSON.stringify({ col, id }));
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragOver={(e) => {
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        setOver((e.clientY - rect.top) > (rect.height / 2) ? "after" : "before");
      }}
      onDragLeave={() => setOver(null)}
      onDrop={onDrop}
      className={`relative group transition ${
        over === "before" ? "border-t-2 border-[#14b8a6] rounded-3xl" :
        over === "after"  ? "border-b-2 border-[#14b8a6] rounded-3xl" : ""
      }`}
    >
      <span
        className="pointer-events-none absolute top-2 right-2 z-10 opacity-0 group-hover:opacity-100 text-[9px] text-white/45 tracking-widest cursor-move select-none bg-black/70 px-1.5 py-0.5 rounded-md border border-white/10 transition-opacity"
        title="Drag to rearrange"
      >
        <i className="fas fa-grip-vertical" />
      </span>
      {children}
    </div>
  );
}

/** Drop area that handles drops on EMPTY columns (when all blocks are gone). */
function DropZone({ col, setOrder, className, children }) {
  const [over, setOver] = useState(false);
  const locked = LEFT_LOCKED && col === "left";
  return (
    <div
      onDragOver={(e) => { if (locked) return; e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (locked) return;
        e.preventDefault();
        setOver(false);
        let src; try { src = JSON.parse(e.dataTransfer.getData("text/plain")); } catch { return; }
        if (!src?.id) return;
        setOrder((prev) => {
          const next = { left: [...prev.left], center: [...prev.center], right: [...prev.right] };
          next[src.col] = next[src.col].filter((x) => x !== src.id);
          if (!next[col].includes(src.id)) next[col].push(src.id);
          return next;
        });
      }}
      className={`${className} ${over ? "outline outline-2 outline-dashed outline-[#14b8a6]/40 rounded-3xl" : ""}`}
    >
      {children}
    </div>
  );
}

export function PlayerDashboard({ onToast, onCompleteProfile, onOpenInbox, onOpenLeague, onCreateLeague, onboardingState, reloadOnboarding }) {
  const navigate = useNavigate();
  const { user, inboxCount, refreshInboxCount } = useAuth();
  const [myLeagues, setMyLeagues] = useState([]);
  const [myTeams, setMyTeams] = useState([]);
  const [threads, setThreads] = useState([]);
  const [invites, setInvites] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [dmTarget, setDmTarget] = useState(null);
  const [hqTeamId, setHqTeamId] = useState(null);
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [recapOpen, setRecapOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [inviteBusyId, setInviteBusyId] = useState("");
  const [friends, setFriends] = useState([]);
  const [friendsOpen, setFriendsOpen] = useState(false);
  const [order, setOrder] = useState(() => loadOrder(user?.dashboard_layout));
  const [leagueUnreadMap, setLeagueUnreadMap] = useState({});  // {league_id: count}
  const [todayRefreshKey, setTodayRefreshKey] = useState(0);
  const todayReloadRef = React.useRef(null);
  const bumpToday = () => setTodayRefreshKey((k) => k + 1);

  // Real-time fan-out: server pushes inbox:new / dm:new / match:* / mention
  // / league_chat:new and we refresh the affected widgets without a full
  // page reload.
  useUserHub((evt) => {
    if (!evt?.type) return;
    if (evt.type === "inbox:new") {
      fetchInbox("pending").then(setInvites).catch(() => {});
      refreshInboxCount?.();
      todayReloadRef.current?.();
      onToast?.(evt.title || "New invite in your inbox.");
    } else if (evt.type === "dm:new") {
      fetchDmThreads().then(setThreads).catch(() => {});
      refreshInboxCount?.();
      todayReloadRef.current?.();
    } else if (evt.type === "match:scheduled" || evt.type === "match:result") {
      fetchMyLeagues().then(setMyLeagues).catch(() => {});
      todayReloadRef.current?.();
    } else if (evt.type === "mention") {
      todayReloadRef.current?.();
      onToast?.(`@${evt.sender_name || "Someone"} mentioned you in ${evt.league_name || "a league"}.`);
    } else if (evt.type === "league_chat:new") {
      setLeagueUnreadMap((prev) => ({ ...prev, [evt.league_id]: (prev[evt.league_id] || 0) + 1 }));
      todayReloadRef.current?.();
    } else if (evt.type === "season_pass:xp") {
      const tierUp = evt.new_tier > evt.prev_tier;
      onToast?.(tierUp
        ? `🎉 +${evt.amount} XP — TIER ${evt.new_tier} UNLOCKED!`
        : `⚡ +${evt.amount} XP`);
    } else if (evt.type === "season_pass:claim") {
      onToast?.(`🎁 Claimed: ${evt.reward || "tier reward"}`);
    }
  });

  // Persist locally + sync to server (debounced) so the layout follows the
  // user across devices.
  useEffect(() => { saveOrder(order); }, [order]);
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => {
      // Skip the initial mount no-op: only push when order differs from
      // what the server already has.
      const same = JSON.stringify(user.dashboard_layout || {}) === JSON.stringify(order);
      if (same) return;
      import("../lib/api").then(({ updateProfile }) =>
        updateProfile({ dashboard_layout: order }).catch(() => { /* offline / 401 fine */ })
      );
    }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  const resetLayout = () => setOrder(DEFAULT_ORDER);

  useEffect(() => {
    if (!user) return;
    fetchMyLeagues().then(setMyLeagues).catch(() => {});
    fetchUserTeams(user.user_id).then(setMyTeams).catch(() => {});
    fetchDmThreads().then(setThreads).catch(() => {});
    fetchInbox("pending").then(setInvites).catch(() => {});
    fetchFriends().then(setFriends).catch(() => {});
    // Seed per-league unread counts so badges show before the first WS event.
    fetchToday().then((t) => {
      const map = {};
      (t?.league_unread || []).forEach((lu) => { map[lu.league_id] = lu.unread; });
      setLeagueUnreadMap(map);
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!myLeagues.length) { setUpcoming([]); return; }
    Promise.all(
      myLeagues.slice(0, 12).map((l) => fetchUpcomingGames(l.league_id).catch(() => []))
    ).then((batches) => {
      const flat = batches
        .flat()
        .sort((a, b) => new Date(a.scheduled_time) - new Date(b.scheduled_time))
        .slice(0, 6);
      setUpcoming(flat);
    });
  }, [myLeagues]);

  const reloadInbox = () => {
    fetchInbox("pending").then(setInvites).catch(() => {});
    refreshInboxCount?.();
  };

  const decideInvite = async (item, action) => {
    setInviteBusyId(item.id);
    try {
      await decideInbox(item.id, action);
      onToast?.(action === "accept" ? "✅ Approved." : "Declined.");
      reloadInbox();
      fetchMyLeagues().then(setMyLeagues).catch(() => {});
      fetchUserTeams(user.user_id).then(setMyTeams).catch(() => {});
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't update invite.");
    } finally { setInviteBusyId(""); }
  };

  if (!user) return null;
  const isFree = !(user.user_type === "pro" || user.plan === "pro" || user.plan === "elite");
  const unreadDm = threads.reduce((n, t) => n + (t.unread_count || 0), 0);

  // ---------------------------------------------------------- block JSX map
  const blocks = {
    today: (
      <TodayTab
        refreshKey={todayRefreshKey}
        registerReload={(fn) => { todayReloadRef.current = fn; }}
        onOpenLeague={onOpenLeague}
        onOpenInbox={onOpenInbox || onCompleteProfile}
        onOpenDms={onCompleteProfile}
        onToast={onToast}
      />
    ),
    profile: (
      <div className="glass rounded-3xl p-5 text-center" data-testid="dash-profile-card">
        <div className="flex justify-center">
          <AvatarChip avatarId={user.avatar_id} pfpUrl={user.pfp_url} size={88} />
        </div>
        <h2 className="font-display text-2xl grad-text mt-3 truncate">{user.name}</h2>
        <p className="text-[10px] tracking-[3px] text-[#a3a3ac] mt-1">
          {(user.user_type || "casual").toUpperCase()} · MMR {user.elo}
        </p>
        <div className="mt-3 flex gap-2 justify-center flex-wrap">
          <button onClick={onCompleteProfile} data-testid="dash-edit-profile"
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-xl btn-ghost">
            <i className="fas fa-pen-to-square mr-1" />EDIT
          </button>
          <button onClick={onCreateLeague} data-testid="dash-create-league"
                  className="text-[10px] tracking-widest px-3 py-1.5 rounded-xl btn-neon">
            <i className="fas fa-trophy mr-1" />NEW LEAGUE
          </button>
        </div>
      </div>
    ),
    pro: isFree ? (
      <Link to="/pricing" data-testid="dash-upgrade-pro-card"
            className="block rounded-3xl p-5 border-2 border-[#14b8a6]/70 bg-gradient-to-br from-[#14b8a6]/15 via-[#14b8a6]/10 to-[#14b8a6]/15 hover:scale-[1.02] transition-transform shadow-[0_0_24px_rgba(253,224,71,0.45)]">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">⭐ FREE TIER</p>
        <h3 className="font-display text-xl text-white mt-1">UPGRADE TO PRO PLAYER</h3>
        <ul className="mt-3 text-[11px] text-[#14b8a6]/85 space-y-1">
          <li>🪄 AI-generated avatars</li>
          <li>📸 Photo PFP uploads</li>
          <li>🩵 Verified blue check</li>
          <li>⚡ Priority queue + stat history</li>
        </ul>
        <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#14b8a6]">
          $1.99/MO · GO PRO <i className="fas fa-arrow-right" />
        </div>
      </Link>
    ) : (
      <div data-testid="dash-pro-perks-card"
           className="rounded-3xl p-5 border-2 border-[#14b8a6]/70 bg-[#14b8a6]/10 shadow-[0_0_24px_rgba(253,224,71,0.45)] text-center">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">👑 PRO PLAYER</p>
        <h3 className="font-display text-xl text-white mt-1">PERKS UNLOCKED</h3>
        <p className="text-[11px] text-[#14b8a6]/80 mt-2">
          AI avatars · PFP uploads · Blue check · Priority queue · Stat history
        </p>
        <CancelMembershipButton onToast={onToast} />
      </div>
    ),
    referral: <ReferralCard onToast={onToast} />,
    public_link: <PublicLinkCard user={user} onToast={onToast} />,
    upcoming_sessions: <UpcomingSessionsCard onToast={onToast} />,
    exports: <PremiumExportsCard onToast={onToast} />,
    friends: (
      <button
        type="button"
        onClick={() => setFriendsOpen(true)}
        data-testid="dash-friends-tile"
        className="w-full glass rounded-3xl p-4 flex items-center gap-3 text-left hover:border-[#14b8a6]/60 border border-transparent transition"
      >
        <div className="w-12 h-12 rounded-2xl bg-[#14b8a6]/15 border border-[#14b8a6]/40 flex items-center justify-center">
          <i className="fas fa-user-group text-[#14b8a6] text-lg" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            FRIENDS
          </p>
          <p className="font-display text-2xl leading-none mt-0.5" data-testid="dash-friends-count">
            {friends.length}
          </p>
          <p className="text-[11px] text-white/55 mt-0.5">
            {friends.length === 0
              ? "No friends yet — explore PLAYERS"
              : "Tap to manage · block · unblock"}
          </p>
        </div>
        <i className="fas fa-chevron-right text-white/40 text-xs" />
      </button>
    ),
    shortcuts: (
      <nav className="glass rounded-3xl p-3" data-testid="dash-shortcuts">
        {[
          { to: "/players", icon: "fa-user-astronaut", label: "PLAYERS" },
          { to: "/leagues", icon: "fa-trophy", label: "LEAGUES" },
          { to: "/lfg", icon: "fa-satellite-dish", label: "LFG" },
          { to: "/pricing", icon: "fa-gem", label: "PRICING" },
          { to: "/contact", icon: "fa-envelope-open-text", label: "CONTACT" },
        ].map((n) => (
          <Link key={n.to} to={n.to}
                className="block px-3 py-2 text-xs tracking-widest text-white/70 hover:text-[#14b8a6] rounded-xl hover:bg-white/5">
            <i className={`fas ${n.icon} w-5 text-[#14b8a6]`} /> {n.label}
          </Link>
        ))}
      </nav>
    ),

    greeting: (
      <header data-testid="dash-greeting">
        <SeasonPassBanner />
        <h1 className="font-display text-4xl md:text-5xl grad-text mt-3">
          GG, {(user?.name || "operator").split(" ")[0]}.
        </h1>
        <p className="text-[#a3a3ac] mt-3 text-sm">
          {unreadDm > 0
            ? <>You have <b className="text-[#14b8a6]">{unreadDm}</b> unread DM{unreadDm === 1 ? "" : "s"} and <b>{invites.length}</b> pending invite{invites.length === 1 ? "" : "s"}.</>
            : invites.length > 0
              ? <>You have <b className="text-[#14b8a6]">{invites.length}</b> pending invite{invites.length === 1 ? "" : "s"}.</>
              : <>All caught up. Queue up a match below.</>}
        </p>
        <GracePeriodBanner user={user} onToast={onToast} />
        {user?.user_id && (
          <button
            type="button"
            onClick={() => setRecapOpen(true)}
            data-testid="dash-share-recap-btn"
            className="mt-3 inline-flex items-center gap-1.5 text-[10px] tracking-[3px] font-bold px-3 py-1.5 rounded-xl bg-[#14b8a6]/15 border border-[#14b8a6]/40 text-[#14b8a6] hover:bg-[#14b8a6]/25"
          >
            <i className="fas fa-share-nodes" />SHARE SEASON RECAP
          </button>
        )}
      </header>
    ),
    card: <YourCardPreview user={user} />,
    glory: (
      <div className="glass rounded-3xl p-5" data-testid="dash-glory-block">
        <GloryBadgesGrid mine onToast={onToast} />
      </div>
    ),
    upcoming: (
      <div className="glass rounded-3xl p-5" data-testid="dash-upcoming-block">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-calendar-days mr-1" /> UPCOMING MATCHES
          </p>
          <button
            type="button"
            onClick={() => setScheduleOpen(true)}
            data-testid="dash-schedule-match-btn"
            className="text-[10px] tracking-widest px-3 py-1 rounded-xl btn-neon"
          >
            <i className="fas fa-plus mr-1" />SCHEDULE
          </button>
        </div>
        {upcoming.length === 0 ? (
          <p className="text-[12px] text-white/45 text-center py-4">
            No matches scheduled. Tap SCHEDULE to invite a team.
          </p>
        ) : (
          <div className="space-y-2">
            {upcoming.map((g, i) => {
              const dt = g.scheduled_time ? new Date(g.scheduled_time) : null;
              const month = dt ? dt.toLocaleString(undefined, { month: "short" }).toUpperCase() : "—";
              const day = dt ? dt.getDate() : "—";
              const time = dt ? dt.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "TBD";
              const key = g.match_id || g.challenge_id || `${g.league_id}:${g.scheduled_time}:${i}`;
              return (
                <div
                  key={key}
                  data-testid={`dash-upcoming-${g.match_id || g.challenge_id || i}`}
                  className="flex items-stretch gap-3 p-2 rounded-2xl bg-black/30 border border-white/10 hover:border-[#14b8a6]/50 transition-colors"
                >
                  {/* Calendar tile */}
                  <button
                    type="button"
                    onClick={() => onOpenLeague?.(g.league_id)}
                    title="Open league"
                    className="shrink-0 w-14 rounded-xl bg-[#14b8a6]/10 border border-[#14b8a6]/40 flex flex-col items-center justify-center py-1 hover:bg-[#14b8a6]/15"
                  >
                    <span className="text-[9px] tracking-[2px] text-[#14b8a6] font-bold">{month}</span>
                    <span className="font-display text-2xl leading-none text-white">{day}</span>
                  </button>
                  {/* Match identity */}
                  <button
                    type="button"
                    onClick={() => onOpenLeague?.(g.league_id)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <p className="text-sm font-bold text-white truncate">
                      {g.team_a_name || g.challenger_name}
                      <span className="text-white/40 mx-1.5">vs</span>
                      {g.team_b_name || g.target_name}
                    </p>
                    <p className="text-[10px] text-[#a3a3ac] truncate">
                      <i className="far fa-clock mr-1" />{time}
                      <span className="text-white/30 mx-1.5">·</span>
                      <i className="fas fa-trophy mr-1" />{g.league_name}
                    </p>
                  </button>
                  {/* One-tap challenge — opens schedule modal pre-populated */}
                  <button
                    type="button"
                    onClick={() => setScheduleOpen(true)}
                    title="Schedule another match"
                    data-testid={`dash-upcoming-challenge-${g.match_id}`}
                    className="shrink-0 self-center w-9 h-9 rounded-xl bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 flex items-center justify-center"
                  >
                    <i className="fas fa-bolt text-xs" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    ),

    inbox: (
      <InboxBlock
        threads={threads}
        invites={invites}
        inboxCount={inboxCount}
        inviteBusyId={inviteBusyId}
        onDecide={decideInvite}
        onOpenDm={(t) => setDmTarget(t.other)}
        onOpenInbox={onOpenInbox || onCompleteProfile}
      />
    ),
    recent: (
      <RecentResultsBlock
        onToast={onToast}
        refreshKey={todayRefreshKey}
      />
    ),
    rivals: <RivalsCard userId={user?.user_id} />,
    clips: <MyClipsBlock />,
    leagues: (
      <div className="glass rounded-3xl p-4" data-testid="dash-my-leagues">
        <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold mb-2">
          <i className="fas fa-trophy mr-1" /> YOUR LEAGUES · {myLeagues.length}
        </p>
        {myLeagues.length === 0 ? (
          <p className="text-[11px] text-white/45">Not in any leagues yet — browse <Link to="/leagues" className="text-[#14b8a6]">LEAGUES</Link>.</p>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {myLeagues.slice(0, 8).map((l) => {
              const unread = leagueUnreadMap[l.league_id] || 0;
              const onOpen = () => {
                // Clear local badge optimistically and tell the server.
                if (unread > 0) {
                  setLeagueUnreadMap((p) => ({ ...p, [l.league_id]: 0 }));
                  markLeagueRead(l.league_id).catch(() => {});
                  bumpToday();
                }
                navigate(`/${l.slug || l.league_id}`);
              };
              return (
                <button key={l.league_id} type="button"
                        onClick={onOpen}
                        data-testid={`dash-league-${l.league_id}`}
                        className={`w-full text-left text-[11px] flex items-center gap-2 p-2 rounded-xl bg-black/20 hover:bg-[#14b8a6]/10 border transition-colors ${unread > 0 ? "border-[#14b8a6]/50" : "border-transparent hover:border-[#14b8a6]/40"}`}>
                  {l.is_owner && <i className="fas fa-crown text-[#14b8a6] text-[10px]" />}
                  <span className={`flex-1 truncate ${unread > 0 ? "font-bold text-white" : "text-white/85"}`}>{l.name}</span>
                  {unread > 0 && (
                    <span data-testid={`dash-league-unread-${l.league_id}`}
                          className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#14b8a6] text-black text-[9px] font-bold flex items-center justify-center">
                      {unread > 9 ? "9+" : unread}
                    </span>
                  )}
                  <span className="text-[#a3a3ac]/60 text-[9px]">{l.game}</span>
                  <i className="fas fa-arrow-right text-[14px] text-[#14b8a6]" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    ),
    hq: (
      <div className="glass rounded-3xl p-4 border border-[#14b8a6]/40" data-testid="dash-team-hq-block">
        <div className="flex items-center justify-between mb-1">
          <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
            <i className="fas fa-flag mr-1" /> YOUR TEAMS · {myTeams.length}
          </p>
          <button
            type="button"
            onClick={() => setCreateTeamOpen(true)}
            data-testid="dash-create-team-btn"
            className="text-[9px] tracking-widest font-bold px-2.5 py-1 rounded-md bg-[#14b8a6]/15 border border-[#14b8a6]/45 text-[#14b8a6] hover:bg-[#14b8a6]/25 transition-colors"
          >
            <i className="fas fa-plus mr-1" />NEW TEAM
          </button>
        </div>
        <p className="text-[10px] text-white/45 mb-2 leading-snug">
          Teams you're on. Owners build per-game rosters with captains. You
          can start a standalone team — no league needed.
        </p>
        {myTeams.length === 0 ? (
          <button
            type="button"
            onClick={() => setCreateTeamOpen(true)}
            data-testid="dash-create-team-empty"
            className="w-full mt-1 text-[11px] py-3 rounded-xl border border-dashed border-[#14b8a6]/40 text-[#14b8a6] hover:bg-[#14b8a6]/10 transition-colors"
          >
            <i className="fas fa-plus mr-2" />FOUND YOUR TEAM
          </button>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
            {myTeams.slice(0, 8).map((t) => (
              <button key={t.team_id} type="button"
                      onClick={() => setHqTeamId(t.team_id)}
                      data-testid={`dash-hq-${t.team_id}`}
                      className="w-full text-left text-[11px] flex items-center gap-2 p-2 rounded-xl bg-black/30 hover:bg-[#14b8a6]/15 border border-transparent hover:border-[#14b8a6]/40 transition-colors">
                {t.is_manager && <i className="fas fa-crown text-[#14b8a6] text-[10px]" />}
                <span className="flex-1 truncate text-white/85">
                  {t.name}
                  {t.game && (
                    <span data-testid={`dash-hq-${t.team_id}-game`}
                          className="ml-1.5 text-[9px] tracking-widest text-[#14b8a6]">
                      · {t.game.toUpperCase()}
                    </span>
                  )}
                </span>
                {t.tier && <TierBadge tier={t.tier} mmr={t.mmr} compact />}
                <i className="fas fa-up-right-from-square text-[12px] text-[#14b8a6]" title="Open HQ" />
              </button>
            ))}
          </div>
        )}
      </div>
    ),
  };

  return (
    <main className="page-z" data-testid="player-dashboard">
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-8">
        {onboardingState && !onboardingState.completed_at && !onboardingState.dismissed && (
          <div className="mb-6">
            <OnboardingChecklist
              state={onboardingState}
              onChange={() => reloadOnboarding?.()}
              onToast={onToast}
            />
          </div>
        )}

        <div className="mb-6">
          <SampleLeagueCta myLeaguesCount={myLeagues.length} onToast={onToast} />
        </div>

        <LiveStreamersTicker />

        <div className="hidden md:flex items-center justify-between mb-3" data-testid="dash-layout-toolbar">
          <p className="text-[10px] tracking-[3px] text-white/40">
            <i className="fas fa-arrows-up-down-left-right mr-1" />
            Drag center & right blocks to rearrange. Left rail is pinned.
          </p>
          <button type="button" onClick={resetLayout} data-testid="dash-reset-layout"
                  className="text-[10px] tracking-widest px-2.5 py-1 rounded-lg btn-ghost">
            <i className="fas fa-rotate-left mr-1" />RESET LAYOUT
          </button>
        </div>

        <div className="grid lg:grid-cols-[280px_1fr_320px] gap-6">
          {["left", "center", "right"].map((col) => {
            const locked = LEFT_LOCKED && col === "left";
            return (
              <DropZone key={col} col={col} setOrder={setOrder}
                        className={`min-w-0 space-y-${col === "left" ? "4" : col === "center" ? "6" : "5"}`}>
                {order[col].map((id) => {
                  const el = blocks[id];
                  if (!el) return null;
                  if (locked) {
                    // Left column is pinned — render the block plainly, no
                    // drag handle, no drop indicator. Keeps profile + PRO +
                    // referral + shortcuts in a stable, predictable spot.
                    return <div key={id} data-testid={`dash-pinned-${id}`}>{el}</div>;
                  }
                  return (
                    <DraggableBlock key={id} id={id} col={col} setOrder={setOrder}>
                      {el}
                    </DraggableBlock>
                  );
                })}
              </DropZone>
            );
          })}
        </div>

        {/* Performance / progression details rendered BELOW the main 3-col
            grid so the dashboard's "above the fold" stays focused on the
            greeting + holo card + glory badges, matching the design
            reference. Users who want to inspect their tier curve or combat
            stats scroll down. */}
        <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dash-progress-stats-row">
          <TierProgressBar className="lg:col-span-1" />
          <div className="lg:col-span-2">
            <CombatStatsCard onToast={onToast} onPickLeague={onOpenLeague} />
          </div>
        </div>

        {user?.user_id && (
          <div className="mt-6">
            <ClutchMomentsRail
              userId={user.user_id}
              onOpenLeague={onOpenLeague}
            />
          </div>
        )}

        {/* Tip-leaderboard (hidden until admin activates tips_enabled). */}
        <div className="mt-6">
          <TipsLeaderboard window="week" limit={5} />
        </div>
      </div>

      {hqTeamId && (
        <TeamHqModal
          teamId={hqTeamId}
          onClose={() => {
            setHqTeamId(null);
            fetchUserTeams(user.user_id).then(setMyTeams).catch(() => {});
          }}
          onToast={onToast}
        />
      )}

      {dmTarget && (
        <DmModal
          targetUser={dmTarget}
          onClose={() => {
            setDmTarget(null);
            fetchDmThreads().then(setThreads).catch(() => {});
            refreshInboxCount?.();
          }}
          onToast={onToast}
        />
      )}

      {scheduleOpen && (
        <ScheduleMatchModal
          myTeams={myTeams.filter((t) => t.is_manager)}
          onClose={() => setScheduleOpen(false)}
          onToast={onToast}
          onCreated={() => {
            // Refresh upcoming list via leagues effect dep change.
            fetchMyLeagues().then(setMyLeagues).catch(() => {});
          }}
        />
      )}

      <FriendsModal
        open={friendsOpen}
        onClose={() => { setFriendsOpen(false); fetchFriends().then(setFriends).catch(() => {}); }}
        onToast={onToast}
        onOpenDm={(u) => setDmTarget(u)}
      />

      <CreateTeamModal
        open={createTeamOpen}
        onClose={() => setCreateTeamOpen(false)}
        onToast={onToast}
        onCreated={(team) => {
          setCreateTeamOpen(false);
          // Reload teams list and pop the HQ for the new team.
          fetchUserTeams(user.user_id).then((ts) => {
            setMyTeams(ts);
            setHqTeamId(team.team_id);
          }).catch(() => {});
        }}
      />

      <SeasonRecapShareModal
        open={recapOpen}
        userId={user?.user_id}
        onClose={() => setRecapOpen(false)}
        onToast={onToast}
      />
    </main>
  );
}


export default PlayerDashboard;
